import {
  CommentaryReviewOutput,
  CostToCureReviewOutput,
  QualityControlOutput,
  expected,
  getActiveFlagCountsBySection,
} from '@aloft/toolkit-shared';
import { Flex } from '@aloft/toolkit-sui';

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';

import { useCertifyModal } from '../../components/CertifyModal/hooks/useCertifyModal.tsx';
import { updateQualityControlStatus } from '../../redux/features/orderSlice.ts';
import {
  selectQualityControlIterations,
  selectQualityControlPassed,
} from '../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { QualityControlReviewContent } from './QualityControlReviewContent.tsx';
import { QUALITY_CONTROL_SUCCESS_DELAY_MS } from './constants.ts';
import { BypassComplianceCheckModal } from './internal/BypassComplianceCheckModal.tsx';
import {
  QualityControlReviewProgressModal,
  ReviewModalPhase,
} from './internal/QualityControlReviewProgressModal.tsx';
import { useQualityControlSync } from './useQualityControlSync.ts';

const reviewHasFlags = (review: CommentaryReviewOutput | CostToCureReviewOutput | undefined) =>
  review != null && 'output' in review && review.output.flags.length > 0;

const hasPendingQualityControlFlags = (output: QualityControlOutput) =>
  Object.values(getActiveFlagCountsBySection(output)).some((count) => count > 0);

export const QualityControlReview = () => {
  const orderID = expected(useParams().orderId);
  const dispatch = useAppDispatch();
  const { qualityControl, isRunningReview, runQualityControl, updateQualityControl } =
    useQualityControlSync(orderID);
  const { certifyModal, openCertifyModal } = useCertifyModal();
  const [reviewModalPhase, setReviewModalPhase] = useState<ReviewModalPhase>('hidden');
  const [isBypassModalOpen, setIsBypassModalOpen] = useState(false);
  const hasOpenedCertifyModal = useRef(false);
  const hasRunReview = useRef(false);
  const certifyAnywayOwnsResultRef = useRef(false);

  const qualityControlPassed = useAppSelector(selectQualityControlPassed);
  const qualityControlIterations = useAppSelector(selectQualityControlIterations);
  const qualityControlIterationsRef = useRef(qualityControlIterations);

  useEffect(() => {
    qualityControlIterationsRef.current = qualityControlIterations;
  }, [qualityControlIterations]);

  useEffect(
    () => () => {
      dispatch(
        updateQualityControlStatus({
          qualityControlPassed: false,
          qualityControlIterations: qualityControlIterationsRef.current,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (isRunningReview) {
      hasRunReview.current = true;
      setReviewModalPhase('running');
      return;
    }

    if (!hasRunReview.current || hasOpenedCertifyModal.current) return;
    if (certifyAnywayOwnsResultRef.current) return;
    setReviewModalPhase(qualityControlPassed ? 'success' : 'hidden');
  }, [isRunningReview, qualityControlPassed]);

  useEffect(() => {
    if (reviewModalPhase !== 'success' || hasOpenedCertifyModal.current) return;

    const timeout = window.setTimeout(() => {
      hasOpenedCertifyModal.current = true;
      setReviewModalPhase('hidden');
      openCertifyModal();
    }, QUALITY_CONTROL_SUCCESS_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [openCertifyModal, reviewModalPhase]);

  const handleCertifyReport = () => {
    if (qualityControl && hasPendingQualityControlFlags(qualityControl)) {
      setIsBypassModalOpen(true);
      return;
    }
    openCertifyModal();
  };

  const handleCertifyAnyway = () => {
    setIsBypassModalOpen(false);
    certifyAnywayOwnsResultRef.current = true;
    void runQualityControl()
      .then((output) => {
        if (
          !reviewHasFlags(output.commentaryReview) &&
          !reviewHasFlags(output.costToCureReview)
        ) {
          setReviewModalPhase('success');
          return;
        }
        hasOpenedCertifyModal.current = true;
        certifyAnywayOwnsResultRef.current = false;
        setReviewModalPhase('hidden');
        openCertifyModal();
      })
      .catch(() => {
        certifyAnywayOwnsResultRef.current = false;
        setReviewModalPhase('hidden');
      });
  };

  return (
    <Flex height="100%" width="100%">
      {certifyModal}
      <BypassComplianceCheckModal
        isOpen={isBypassModalOpen}
        onClose={() => setIsBypassModalOpen(false)}
        onCertifyAnyway={handleCertifyAnyway}
      />
      <QualityControlReviewProgressModal phase={reviewModalPhase} />
      {qualityControl && (
        <QualityControlReviewContent
          qualityControl={qualityControl}
          qualityControlPassed={qualityControlPassed}
          isRunningReview={isRunningReview}
          onRunComplianceCheck={() => {
            void runQualityControl();
          }}
          onCertify={handleCertifyReport}
          updateQualityControl={updateQualityControl}
        />
      )}
    </Flex>
  );
};
